import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import iziToast from "izitoast";
import {
  AlertTriangle,
  BadgeCheck,
  BookmarkPlus,
  CheckCircle2,
  CopyCheck,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Lock,
  RotateCcw,
  Save,
  Send,
  Sparkles,
  XCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  HSN_MIN_CONFIDENCE,
  HSN_SOURCE,
  QUOTE_SOURCE_OPTIMAL,
  QUOTE_STATUS,
  buildQuotationWorksheet,
  normalizeHsn,
  reviewQuotation,
  toCustomerQuote,
  toQuotationItems,
  worksheetFromSaved,
} from "utils/quotationUtils";
import {
  buildQuotationPdfBlob,
  buildQuotationXlsxBlob,
  buildWorksheetXlsxBlob,
  downloadBlob,
  quotationFileName,
  worksheetFileName,
} from "utils/quotationExport";
import {
  getFxRateAPI,
  getQuotationAPI,
  getQuotationSettingsAPI,
  getQuotationsAPI,
  hsnApproveAPI,
  hsnLookupAPI,
  saveHsnRatesAPI,
  saveQuotationAPI,
  submitQuotationAPI,
} from "actions/quotationActions";

const STEPS = ["Sourcing", "Worksheet", "Quote & review"];

const inputCls =
  "h-9 w-full rounded-md border border-s360-stroke1 bg-white px-2.5 text-sm text-s360-fg1 focus:outline-none focus:ring-2 focus:ring-s360-violet/40 disabled:bg-s360-bg2 disabled:text-s360-fg2";
const cellInput =
  "h-7 w-full rounded border border-s360-stroke1 bg-white px-1.5 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-s360-violet/40 disabled:bg-transparent disabled:border-transparent";
const btn =
  "inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-s360-stroke1 bg-white text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2 disabled:opacity-50";
const btnPrimary =
  "inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600 disabled:opacity-50";

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs font-semibold text-s360-fg2">{label}</span>
    {children}
    {hint && <span className="text-[11px] text-s360-fg3">{hint}</span>}
  </label>
);

const money = (n, dp = 2) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
const unit = (n) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 4 });
const trimNum = (n, dp = 4) => String(parseFloat(Number(n || 0).toFixed(dp)));

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const toast = (ok, message) =>
  iziToast[ok ? "success" : "error"]({
    title: ok ? "Done" : "Error",
    message,
    position: "topCenter",
  });

// An editable worksheet cell. `override` is the value typed for this quote
// (undefined = use the computed value); overridden cells are tinted and get a
// reset button.
const OverrideCell = ({ value, override, onChange, disabled, mono, left, width = "w-20", children }) => {
  const overridden = override !== undefined && override !== null && String(override) !== "";
  return (
    <div className={`flex items-center gap-0.5 ${width}`}>
      <input
        className={`${cellInput} ${mono ? "font-mono text-left" : ""} ${left ? "text-left" : ""} ${overridden ? "bg-amber-50 border-amber-300" : ""}`}
        value={overridden ? override : value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {overridden && !disabled && (
        <button
          type="button"
          title="Reset to the default"
          onClick={() => onChange(undefined)}
          className="shrink-0 text-s360-fg3 hover:text-s360-fg1"
        >
          <RotateCcw className="w-3 h-3" />
        </button>
      )}
      {children}
    </div>
  );
};

// Lines per "Fetch HSN & BCD" request: one OpenAI call plus ICEGATE lookups
// must finish inside hsnLookupAPI's request timeout.
const HSN_BATCH = 10;

const WORKSHEET_STEP = 1;

// A line the worksheet should look up on its own: no approved/AI HSN yet, or
// its code has no ICEGATE answer. Codes ICEGATE doesn't know stay as they are.
const needsHsnLookup = (l) =>
  l.hsnSource === HSN_SOURCE.DISTRIBUTOR || !l.hsnSource || !l.tariff || l.tariff.status === "error";

const ICEGATE_URL = "https://www.icegate.gov.in/";

// Where the line's HSN came from, as a small chip under the HSN cell.
const HsnChip = ({ line: l }) => {
  if (l.hsnSource === HSN_SOURCE.AI) {
    const pct = l.hsnConfidence != null ? Math.round(l.hsnConfidence * 100) : null;
    const low = l.hsnConfidence == null || l.hsnConfidence < HSN_MIN_CONFIDENCE;
    return (
      <span
        className={`inline-flex items-center gap-0.5 text-[10px] ${low ? "text-amber-700" : "text-s360-violet"}`}
        title={l.hsnReasoning || "Suggested by AI. Check it before quoting."}
      >
        <Sparkles className="w-3 h-3" /> AI{pct != null ? ` ${pct}%` : ""}
        {low ? " · review" : ""}
      </span>
    );
  }
  // A saved manual code reaches the line through hsnInfo, not as an override.
  if (l.hsnSource === HSN_SOURCE.VERIFIED || (l.hsnSource === HSN_SOURCE.MANUAL && !l.hsnOverridden))
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700">
        <BadgeCheck className="w-3 h-3" /> {l.hsnSource === HSN_SOURCE.VERIFIED ? "Verified" : "Saved"}
      </span>
    );
  if (l.hsnSource === HSN_SOURCE.DISTRIBUTOR)
    return (
      <span className="text-[10px] text-s360-fg3" title="The distributor's US HTS code. Digits 7–8 may not be the Indian tariff item.">
        Distributor (US)
      </span>
    );
  return null;
};

// Where the line's duty % came from, under the Duty % cell.
const DutyNote = ({ line: l }) => {
  if (l.dutySource === "icegate" && l.tariff)
    return (
      <span className="text-[10px] text-emerald-700" title={l.tariff.description || undefined}>
        ICEGATE · BCD {trimNum(l.tariff.bcd_pct, 2)}% + SWS
      </span>
    );
  if (l.tariff?.status === "not_found")
    return <span className="text-[10px] text-amber-700" title="ICEGATE has no tariff line with this code">not in tariff</span>;
  if (l.tariff?.status === "error")
    return <span className="text-[10px] text-amber-700">ICEGATE unavailable</span>;
  return null;
};

const STATUS_STYLE = {
  [QUOTE_STATUS.FOUND]: "text-emerald-700",
  [QUOTE_STATUS.PARTIAL]: "text-amber-700",
  [QUOTE_STATUS.NOT_FOUND]: "text-rose-700",
  [QUOTE_STATUS.INVALID_MPN]: "text-rose-700",
};

const StatusBadge = ({ line: l }) => (
  <span
    className={`font-semibold whitespace-nowrap ${STATUS_STYLE[l.status] ?? "text-s360-fg3"}`}
    title={
      l.status === QUOTE_STATUS.PARTIAL
        ? `Stock ${l.availability.toLocaleString("en-IN")} of ${l.qty.toLocaleString("en-IN")} requested`
        : l.status === QUOTE_STATUS.INVALID_MPN
          ? "No distributor returned this part number"
          : undefined
    }
  >
    {l.status}
  </span>
);

const QuotationDialog = ({
  open,
  onOpenChange,
  bomId,
  rawBomItems,
  bomItems,
  distributorSummary,
  priceByRfqQty,
  bomFileName,
}) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  // SweetAlert confirms must render inside this Radix DialogContent, otherwise
  // the modal's focus/pointer-events trap makes the Swal buttons unclickable.
  const dialogContentRef = useRef(null);

  // Account settings (Settings page): margins + HSN duty table.
  const [account, setAccount] = useState({ settings: {}, hsnRates: [], canEdit: false });
  const [fxUpdatedAt, setFxUpdatedAt] = useState(null);
  const [savedList, setSavedList] = useState([]);

  // This quotation.
  const [quote, setQuote] = useState(null); // { id, status, quote_number }
  const [snapshot, setSnapshot] = useState(null); // submitted quotes show their saved lines
  const [source, setSource] = useState(QUOTE_SOURCE_OPTIMAL);
  const [fxRate, setFxRate] = useState("");
  const [totalFreight, setTotalFreight] = useState("");
  const [defaultMargin, setDefaultMargin] = useState("");
  const [overrides, setOverrides] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [bulkMargin, setBulkMargin] = useState("");
  const [customer, setCustomer] = useState("");
  const [quoteNumber, setQuoteNumber] = useState("");
  const [validUntil, setValidUntil] = useState(addDaysISO(15));
  const [notes, setNotes] = useState("");
  // "Fetch HSN & BCD": per-line HSN (AI/approved) and ICEGATE duty per code.
  // Saved with the draft (overrides.hsn / overrides.tariffs).
  const [hsnInfo, setHsnInfo] = useState({});
  const [tariffs, setTariffs] = useState({});
  const [hsnProgress, setHsnProgress] = useState(null); // { done, total }
  const tariffRequested = useRef(new Set());
  const hsnRunning = useRef(false);

  const locked = quote?.status === "submitted";

  const startNew = useCallback((acct, fx) => {
    setQuote(null);
    setSnapshot(null);
    setSource(QUOTE_SOURCE_OPTIMAL);
    setFxRate(fx != null ? String(fx) : "");
    setTotalFreight("");
    setDefaultMargin(String(acct?.settings?.default_margin_pct ?? 0));
    setOverrides({});
    setHsnInfo({});
    setTariffs({});
    setSelected(new Set());
    setCustomer("");
    setQuoteNumber("");
    setValidUntil(addDaysISO(15));
    setNotes("");
    setStep(0);
  }, []);

  const applySaved = useCallback((q) => {
    setQuote({ id: q.id, status: q.status, quote_number: q.quote_number });
    setSnapshot(q.status === "submitted" ? worksheetFromSaved(q) : null);
    setSource(q.source || QUOTE_SOURCE_OPTIMAL);
    setFxRate(String(Number(q.fx_rate) || ""));
    setTotalFreight(Number(q.total_freight) ? String(Number(q.total_freight)) : "");
    setDefaultMargin(String(q.overrides?.defaultMarginPct ?? ""));
    setOverrides(q.overrides?.lines || {});
    setHsnInfo(q.overrides?.hsn || {});
    setTariffs(q.overrides?.tariffs || {});
    setCustomer(q.customer || "");
    setQuoteNumber(q.quote_number || "");
    setValidUntil(q.valid_until ? String(q.valid_until).slice(0, 10) : addDaysISO(15));
    setNotes(q.notes || "");
    setSelected(new Set());
  }, []);

  const loadQuote = async (id) => {
    setLoading(true);
    try {
      const res = await getQuotationAPI(id);
      if (res?.status) applySaved(res.data.quotation);
      else toast(false, res?.message || "Couldn't open the quotation.");
    } finally {
      setLoading(false);
    }
  };

  // On open: account settings, today's FX rate, and this BOM's saved quotes.
  // The latest draft is reopened so work isn't lost between sessions.
  useEffect(() => {
    if (!open) return undefined;
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const safe = (p) => p.catch(() => null);
        const [s, fx, list] = await Promise.all([
          safe(getQuotationSettingsAPI()),
          safe(getFxRateAPI()),
          bomId ? safe(getQuotationsAPI(bomId)) : Promise.resolve(null),
        ]);
        if (ignore) return;
        const acct = {
          settings: s?.data?.settings || {},
          hsnRates: s?.data?.hsn_rates || [],
          canEdit: !!s?.data?.can_edit,
        };
        setAccount(acct);
        setFxUpdatedAt(fx?.data?.updated_at || null);
        const rows = list?.data?.list || [];
        setSavedList(rows);
        const draft = rows.find((r) => r.status === "draft");
        if (draft) {
          const res = await safe(getQuotationAPI(draft.id));
          if (!ignore && res?.status) {
            applySaved(res.data.quotation);
            return;
          }
        }
        if (!ignore) startNew(acct, fx?.data?.usd_inr_rate);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [open, bomId, applySaved, startNew]);

  const settingsForCalc = useMemo(
    () => ({
      defaultMarginPct: defaultMargin,
      distributorMargins: account.settings.distributor_margins || {},
      defaultDutyPct: account.settings.default_duty_pct,
      hsnRates: account.hsnRates,
    }),
    [defaultMargin, account],
  );

  const worksheet = useMemo(() => {
    if (!open) return null;
    if (snapshot) return snapshot;
    return buildQuotationWorksheet({
      rawBomItems,
      bomItems,
      source,
      fxRate,
      totalFreight,
      settings: settingsForCalc,
      overrides,
      hsnInfo,
      tariffs,
      priceByRfqQty,
    });
  }, [open, snapshot, rawBomItems, bomItems, source, fxRate, totalFreight, settingsForCalc, overrides, hsnInfo, tariffs, priceByRfqQty]);

  // Merge a lookup response into hsnInfo/tariffs. Codes typed on the line
  // (source "typed") only bring their duty; the rest also bring the HSN.
  const mergeLookup = useCallback((items) => {
    const info = {};
    const rates = {};
    (items || []).forEach((r) => {
      if (r.hsn_code && r.hsn_source !== "typed") {
        info[r.key] = {
          hsn_code: r.hsn_code,
          hsn_source: r.hsn_source,
          confidence: r.confidence ?? null,
          reasoning: r.reasoning ?? "",
        };
      }
      if (r.tariff?.hsn_code) rates[r.tariff.hsn_code] = r.tariff;
    });
    if (Object.keys(info).length) setHsnInfo((prev) => ({ ...prev, ...info }));
    if (Object.keys(rates).length) setTariffs((prev) => ({ ...prev, ...rates }));
  }, []);

  // A typed 8-digit HSN gets its ICEGATE duty shortly after typing stops.
  useEffect(() => {
    if (!open || snapshot) return undefined;
    const partById = Object.fromEntries((bomItems || []).map((b) => [b.id, b.part_number]));
    const pending = Object.entries(overrides)
      .map(([key, ov]) => ({ key, code: normalizeHsn(ov?.hsn) }))
      .filter(({ code }) => code.length === 8 && !tariffs[code] && !tariffRequested.current.has(code));
    if (!pending.length) return undefined;
    const timer = setTimeout(async () => {
      pending.forEach(({ code }) => tariffRequested.current.add(code));
      const res = await hsnLookupAPI(
        pending.map(({ key, code }) => ({ key: String(key), mpn: String(partById[key] || key), hsn: code })),
      ).catch(() => null);
      if (res?.status) mergeLookup(res.data.items);
      else pending.forEach(({ code }) => tariffRequested.current.delete(code));
    }, 700);
    return () => clearTimeout(timer);
  }, [open, snapshot, overrides, tariffs, bomItems, mergeLookup]);

  // HSN from the account's approvals / AI (OpenAI) and BCD from ICEGATE, a few
  // lines per request. A failed batch is retried once (what the server finished
  // is cached, so the retry is quick) and doesn't stop the rest.
  const lookupHsn = useCallback(async (targets, { quiet = false } = {}) => {
    if (!targets.length || hsnRunning.current) return;
    hsnRunning.current = true;
    setHsnProgress({ done: 0, total: targets.length });
    let failed = null;
    try {
      for (let i = 0; i < targets.length; i += HSN_BATCH) {
        const batch = targets.slice(i, i + HSN_BATCH).map((l) => ({
          key: String(l.key),
          mpn: String(l.quotedMpn || l.mpn),
          make: l.quotedMake || l.manufacturer || "",
          description: String(l.productDescription || l.description || "").slice(0, 1000),
          hts_hint: l.htsHint || "",
          hsn: l.hsnOverridden ? l.hsnCode : "",
        }));
        /* eslint-disable no-await-in-loop */
        let res = await hsnLookupAPI(batch).catch(() => null);
        if (!res?.status) res = await hsnLookupAPI(batch).catch(() => null);
        /* eslint-enable no-await-in-loop */
        if (res?.status) {
          mergeLookup(res.data.items);
          if (res.data.error) failed = res.data.error;
        } else {
          failed = res?.message || "Couldn't fetch HSN codes for some lines.";
        }
        setHsnProgress({ done: Math.min(i + HSN_BATCH, targets.length), total: targets.length });
      }
    } finally {
      hsnRunning.current = false;
      setHsnProgress(null);
    }
    if (failed) toast(false, failed);
    else if (!quiet) toast(true, `HSN and duty fetched for ${targets.length} line${targets.length > 1 ? "s" : ""}`);
  }, [mergeLookup]);

  // Opening the Worksheet step fills in whatever is missing: lines with no
  // approved/AI HSN yet, and codes with no ICEGATE rate.
  const worksheetRef = useRef(null);
  worksheetRef.current = worksheet;
  const hasWorksheet = !!worksheet;
  useEffect(() => {
    if (!open || step !== WORKSHEET_STEP || !hasWorksheet || snapshot || locked) return;
    lookupHsn(worksheetRef.current.lines.filter((l) => l.quoted && needsHsnLookup(l)), { quiet: true });
  }, [open, step, hasWorksheet, snapshot, locked, quote?.id, lookupHsn]);

  const issues = useMemo(
    () => (worksheet ? reviewQuotation(worksheet, { fxRate, fxUpdatedAt: snapshot ? null : fxUpdatedAt }) : []),
    [worksheet, fxRate, fxUpdatedAt, snapshot],
  );

  if (!worksheet) return null;

  const customerQuote = toCustomerQuote(worksheet);
  const hasErrors = issues.some((i) => i.level === "error");
  const srcCur = worksheet.sourceCurrency || "USD";

  const setOv = (key, field, value) =>
    setOverrides((prev) => {
      const line = { ...(prev[key] || {}) };
      if (value === undefined) delete line[field];
      else line[field] = value;
      const next = { ...prev };
      if (Object.keys(line).length) next[key] = line;
      else delete next[key];
      return next;
    });

  // Step 9 helpers: copy a line's duty % to every line with the same HSN, and
  // (owner/admin) remember it in the account's HSN table.
  const applyDutyToHsn = (hsn, pct) => {
    worksheet.lines
      .filter((l) => l.quoted && l.hsnCode === hsn)
      .forEach((l) => setOv(l.key, "dutyPct", String(pct)));
  };
  const saveDutyToAccount = async (hsn, pct) => {
    const res = await saveHsnRatesAPI([{ hsn_code: hsn, duty_pct: Number(pct) }]);
    if (!res?.status) return toast(false, res?.message || "Couldn't save the HSN code.");
    const saved = res.data.rates || [];
    setAccount((a) => {
      const byCode = new Map(a.hsnRates.map((r) => [r.hsn_code, r]));
      saved.forEach((r) => byCode.set(r.hsn_code, r));
      return { ...a, hsnRates: [...byCode.values()] };
    });
    toast(true, `HSN ${hsn} → ${pct}% saved to the account`);
  };

  // The button: the selected lines, or every priced line.
  const fetchHsn = () =>
    lookupHsn(worksheet.lines.filter((l) => l.quoted && (selected.size === 0 || selected.has(l.key))));

  // Owner/admin: remember this line's HSN for the part on future quotes.
  const approveHsn = async (l) => {
    const res = await hsnApproveAPI({
      mpn: l.quotedMpn || l.mpn,
      make: l.quotedMake || l.manufacturer || "",
      hsn_code: l.hsnCode,
      source: l.hsnOverridden ? HSN_SOURCE.MANUAL : HSN_SOURCE.VERIFIED,
    });
    if (!res?.status) return toast(false, res?.message || "Couldn't save the HSN code.");
    const { hsn_code: code, hsn_source: src, tariff } = res.data;
    setHsnInfo((prev) => ({ ...prev, [l.key]: { hsn_code: code, hsn_source: src, confidence: null, reasoning: "" } }));
    if (tariff?.hsn_code) setTariffs((prev) => ({ ...prev, [tariff.hsn_code]: tariff }));
    if (l.hsnOverridden) setOv(l.key, "hsn", undefined);
    toast(true, `HSN ${code} saved for ${l.quotedMpn || l.mpn}`);
  };

  const applyBulkMargin = () => {
    if (bulkMargin === "") return;
    selected.forEach((key) => setOv(key, "marginPct", bulkMargin));
    setSelected(new Set());
    setBulkMargin("");
  };

  const meta = {
    quoteNumber: quote?.quote_number || quoteNumber,
    customer,
    validUntil,
    notes,
    currency: "INR",
    bomFileName,
    date: todayISO(),
    fxRate,
    totalFreight,
  };

  const refreshList = async () => {
    if (!bomId) return;
    const list = await getQuotationsAPI(bomId).catch(() => null);
    if (list?.status) setSavedList(list.data.list || []);
  };

  const save = async ({ quiet = false } = {}) => {
    setBusy("save");
    try {
      const res = await saveQuotationAPI({
        id: quote?.id,
        bom_id: bomId,
        quote_number: quoteNumber,
        customer,
        valid_until: validUntil || null,
        notes,
        fx_rate: Number(fxRate),
        total_freight: Number(totalFreight) || 0,
        source,
        source_currency: worksheet.sourceCurrency,
        overrides: { lines: overrides, defaultMarginPct: defaultMargin, hsn: hsnInfo, tariffs },
        items: toQuotationItems(worksheet),
      });
      if (!res?.status) {
        toast(false, res?.message || "Couldn't save the quotation.");
        return null;
      }
      const q = res.data.quotation;
      setQuote({ id: q.id, status: q.status, quote_number: q.quote_number });
      setQuoteNumber(q.quote_number);
      if (!quiet) toast(true, `Draft ${q.quote_number} saved`);
      refreshList();
      return q;
    } finally {
      setBusy(null);
    }
  };

  const submit = async () => {
    const warnCount = issues.filter((i) => i.level === "warn").length;
    const { isConfirmed } = await Swal.fire({
      title: "Submit this quotation?",
      html: `Total <b>INR ${money(customerQuote.total)}</b> for ${customerQuote.quotedCount} lines.${
        warnCount ? `<br/><br/>${warnCount} review warning(s) are still open.` : ""
      }<br/><br/>A submitted quotation is locked and can't be edited.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Submit",
      confirmButtonColor: "#5F47C9",
      // Render inside the Radix DialogContent so the modal's focus/pointer-events
      // trap doesn't make these buttons unclickable.
      target: dialogContentRef.current ?? undefined,
      heightAuto: false,
    });
    if (!isConfirmed) return;
    const q = await save({ quiet: true });
    if (!q) return;
    setBusy("submit");
    try {
      const res = await submitQuotationAPI(q.id);
      if (!res?.status) return toast(false, res?.message || "Couldn't submit the quotation.");
      applySaved(res.data.quotation);
      toast(true, `Quotation ${q.quote_number} submitted`);
      refreshList();
    } finally {
      setBusy(null);
    }
  };

  const download = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "worksheet") {
        downloadBlob(await buildWorksheetXlsxBlob(meta, worksheet), worksheetFileName(meta));
      } else if (kind === "pdf") {
        downloadBlob(buildQuotationPdfBlob(meta, customerQuote), quotationFileName(meta, "pdf"));
      } else {
        downloadBlob(await buildQuotationXlsxBlob(meta, customerQuote), quotationFileName(meta, "xlsx"));
      }
    } finally {
      setBusy(null);
    }
  };

  const onPickSaved = (value) => {
    if (value === "new") startNew(account, fxRate || null);
    else loadQuote(Number(value));
  };

  const q = worksheet.lines.filter((l) => l.quoted);
  const countOf = (st) => worksheet.lines.filter((l) => l.status === st).length;
  const partial = countOf(QUOTE_STATUS.PARTIAL);
  const invalidMpn = countOf(QUOTE_STATUS.INVALID_MPN);
  const t = worksheet.totals;

  // ---------------------------------------------------------------- steps --

  const sourcing = (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <Field label="Price source">
            <select className={inputCls} value={source} disabled={locked} onChange={(e) => setSource(e.target.value)}>
              <option value={QUOTE_SOURCE_OPTIMAL}>Optimized mix (best price per line)</option>
              {(distributorSummary || []).map((d) => (
                <option key={d.distributor} value={d.distributor}>
                  {d.distributor} only
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="flex gap-2 text-xs">
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">{q.length - partial} in stock</span>
          <span className="rounded-full bg-amber-50 px-2.5 py-1 font-semibold text-amber-700">{partial} partial found</span>
          <span className="rounded-full bg-rose-50 px-2.5 py-1 font-semibold text-rose-700">
            {countOf(QUOTE_STATUS.NOT_FOUND)} not found
          </span>
          <span className="rounded-full bg-rose-50 px-2.5 py-1 font-semibold text-rose-700">{invalidMpn} invalid MPN</span>
        </div>
      </div>
      <div className="max-h-[52vh] overflow-auto rounded-md border border-s360-stroke1">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-s360-bg2 text-left text-s360-fg3">
            <tr>
              {["#", "MPN", "Req qty", "Distributor", `Unit price (${srcCur})`, "Stock", "MOQ", "SPQ", "Status"].map((h, i) => (
                <th key={h} className={`px-2.5 py-1.5 font-semibold ${i >= 2 && i <= 7 ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-s360-bg2">
            {worksheet.lines.map((l) => (
              <tr key={l.key} className={l.quoted ? "" : "text-s360-fg3"}>
                <td className="px-2.5 py-1.5">{l.slNo}</td>
                <td className="px-2.5 py-1.5 font-mono">
                  {l.mpn}
                  {l.quoted && l.quotedMpn && String(l.quotedMpn).toUpperCase() !== String(l.mpn).toUpperCase() && (
                    <div className="text-[10px] text-amber-700">quoted as {l.quotedMpn}</div>
                  )}
                </td>
                <td className="px-2.5 py-1.5 text-right tabular-nums">{l.qty.toLocaleString("en-IN")}</td>
                <td className="px-2.5 py-1.5 text-right">{l.distributor || "—"}</td>
                <td className="px-2.5 py-1.5 text-right tabular-nums">{l.quoted ? unit(l.buyUnit) : "—"}</td>
                <td className={`px-2.5 py-1.5 text-right tabular-nums ${l.status === QUOTE_STATUS.PARTIAL ? "text-amber-700 font-semibold" : ""}`}>
                  {l.quoted ? l.availability.toLocaleString("en-IN") : "—"}
                </td>
                <td className="px-2.5 py-1.5 text-right tabular-nums">{l.quoted ? l.moq : "—"}</td>
                <td className="px-2.5 py-1.5 text-right tabular-nums">{l.quoted ? l.spq : "—"}</td>
                <td className="px-2.5 py-1.5">
                  <StatusBadge line={l} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const worksheetStep = (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field
          label={`FX rate (1 ${srcCur} = ? INR)`}
          hint={fxUpdatedAt && !locked ? `Daily rate updated ${new Date(fxUpdatedAt).toLocaleString("en-IN")}. Editable.` : "Today's rate. Editable."}
        >
          <input type="number" min="0" step="0.01" className={inputCls} disabled={locked} value={fxRate} onChange={(e) => setFxRate(e.target.value)} />
        </Field>
        <Field label="Freight forwarder charges (INR, total)" hint="Split across lines by INR value. Override any line below.">
          <input type="number" min="0" step="1" className={inputCls} disabled={locked} value={totalFreight} onChange={(e) => setTotalFreight(e.target.value)} placeholder="0" />
        </Field>
        <Field
          label="Default margin % (this quote)"
          hint={`Account default ${Number(account.settings.default_margin_pct ?? 0)}%. Distributor margins from the Settings page apply first.`}
        >
          <input type="number" min="0" step="0.5" className={inputCls} disabled={locked} value={defaultMargin} onChange={(e) => setDefaultMargin(e.target.value)} />
        </Field>
      </div>

      {!locked && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button type="button" className={btn} disabled={!!hsnProgress || q.length === 0} onClick={fetchHsn}>
            <Sparkles className="w-4 h-4" />
            {hsnProgress
              ? `Fetching ${hsnProgress.done}/${hsnProgress.total}…`
              : `Fetch HSN & BCD${selected.size > 0 ? ` (${selected.size} selected)` : ""}`}
          </button>
          <span className="text-s360-fg3">
            Fetched automatically from your approved codes or AI, with BCD from ICEGATE. AI codes are suggestions, so check them before submitting.
          </span>
        </div>
      )}

      {selected.size > 0 && !locked && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-s360-violet-50 px-3 py-2 text-xs">
          <strong>{selected.size} selected</strong>
          <span className="text-s360-fg2">· Set margin %</span>
          <input type="number" min="0" step="0.5" className={`${cellInput} w-20`} value={bulkMargin} onChange={(e) => setBulkMargin(e.target.value)} />
          <button type="button" onClick={applyBulkMargin} className="rounded bg-s360-violet px-2.5 py-1 font-semibold text-white">
            Apply
          </button>
          <button type="button" onClick={() => setSelected(new Set())} className="text-s360-fg2 underline">
            Clear
          </button>
        </div>
      )}

      <div className="max-h-[50vh] overflow-auto rounded-md border border-s360-stroke1">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-s360-bg2 text-s360-fg3">
            <tr className="text-right">
              <th className="px-2 py-1.5">
                {!locked && (
                  <input
                    type="checkbox"
                    aria-label="Select all lines"
                    checked={q.length > 0 && selected.size === q.length}
                    onChange={(e) => setSelected(e.target.checked ? new Set(q.map((l) => l.key)) : new Set())}
                  />
                )}
              </th>
              <th className="px-2 py-1.5 text-left font-semibold">#</th>
              <th className="px-2 py-1.5 text-left font-semibold">MPN</th>
              <th className="px-2 py-1.5 font-semibold">Qty</th>
              <th className="px-2 py-1.5 text-left font-semibold">Distributor HSN</th>
              <th className="px-2 py-1.5 text-left font-semibold">HSN</th>
              <th className="px-2 py-1.5 font-semibold">Unit ({srcCur})</th>
              <th className="px-2 py-1.5 font-semibold">INR</th>
              <th className="px-2 py-1.5 font-semibold">Duty %</th>
              <th className="px-2 py-1.5 font-semibold">Duty</th>
              <th className="px-2 py-1.5 font-semibold">Freight (line)</th>
              <th className="px-2 py-1.5 font-semibold">Landing</th>
              <th className="px-2 py-1.5 font-semibold">Margin %</th>
              <th className="px-2 py-1.5 font-semibold">Margin</th>
              <th className="px-2 py-1.5 font-semibold">Resell</th>
              <th className="px-2 py-1.5 font-semibold">Line total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-s360-bg2">
            {worksheet.lines.map((l) => {
              if (!l.quoted)
                return (
                  <tr key={l.key} className="text-s360-fg3">
                    <td />
                    <td className="px-2 py-1">{l.slNo}</td>
                    <td className="px-2 py-1 font-mono">{l.mpn}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{l.qty.toLocaleString("en-IN")}</td>
                    <td colSpan={12} className="px-2 py-1 italic">{l.status === QUOTE_STATUS.INVALID_MPN ? "Invalid MPN" : "Not found"}: quoted as TBA</td>
                  </tr>
                );
              const ov = overrides[l.key] || {};
              const dutyFromLine = l.dutySource === "line" && l.hsnCode;
              return (
                <tr key={l.key} className={selected.has(l.key) ? "bg-s360-violet-50/60" : ""}>
                  <td className="px-2 py-1 text-center">
                    {!locked && (
                      <input
                        type="checkbox"
                        aria-label={`Select line ${l.slNo}`}
                        checked={selected.has(l.key)}
                        onChange={(e) =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(l.key);
                            else next.delete(l.key);
                            return next;
                          })
                        }
                      />
                    )}
                  </td>
                  <td className="px-2 py-1">{l.slNo}</td>
                  <td className="px-2 py-1 font-mono">
                    {l.mpn}
                    <div className="text-[10px] text-s360-fg3 font-sans">{l.distributor}</div>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{l.qty.toLocaleString("en-IN")}</td>
                  <td
                    className="px-2 py-1 font-mono text-s360-fg2"
                    title={l.htsHint ? "From the distributor. This is a US code: digits 7–8 may not be the Indian tariff item." : undefined}
                  >
                    {l.htsHint || "-"}
                  </td>
                  <td className="px-2 py-1">
                    <OverrideCell mono width="w-24" value={l.hsnCode} override={ov.hsn} disabled={locked} onChange={(v) => setOv(l.key, "hsn", v)} />
                    <div className="flex items-center gap-1.5">
                      <HsnChip line={l} />
                      {!locked && account.canEdit && normalizeHsn(l.hsnCode).length === 8 &&
                        (l.hsnSource === HSN_SOURCE.AI || l.hsnOverridden) && (
                          <button
                            type="button"
                            className="text-[10px] text-s360-violet hover:underline"
                            title={`Use HSN ${l.hsnCode} for ${l.quotedMpn || l.mpn} on future quotes`}
                            onClick={() => approveHsn(l)}
                          >
                            approve
                          </button>
                        )}
                      {normalizeHsn(l.hsnCode).length === 8 && (
                        <a
                          href={ICEGATE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-s360-fg3 hover:text-s360-fg1"
                          title="Verify on ICEGATE"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{unit(l.buyUnit)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{unit(l.inrUnit)}</td>
                  <td className="px-2 py-1">
                    <div className="flex flex-col items-end">
                      <OverrideCell
                        width="w-[4.5rem]"
                        value={l.dutySource === null ? "" : trimNum(l.dutyPct)}
                        override={ov.dutyPct}
                        disabled={locked}
                        onChange={(v) => setOv(l.key, "dutyPct", v)}
                      />
                      {l.dutySource === null && !l.tariff && <span className="text-[10px] text-amber-700">no HSN match</span>}
                      {l.dutySource !== "line" && <DutyNote line={l} />}
                      {dutyFromLine && !locked && (
                        <span className="flex gap-1.5 text-[10px]">
                          <button type="button" className="text-s360-violet hover:underline" title={`Use ${l.dutyPct}% on every line with HSN ${l.hsnCode}`} onClick={() => applyDutyToHsn(l.hsnCode, l.dutyPct)}>
                            <CopyCheck className="inline w-3 h-3" /> same HSN
                          </button>
                          {account.canEdit && (
                            <button type="button" className="text-s360-violet hover:underline" title={`Save HSN ${l.hsnCode} → ${l.dutyPct}% to the account table`} onClick={() => saveDutyToAccount(l.hsnCode, l.dutyPct)}>
                              <BookmarkPlus className="inline w-3 h-3" /> save
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{unit(l.dutyUnit)}</td>
                  <td className="px-2 py-1">
                    <div className="flex justify-end">
                      <OverrideCell width="w-24" value={trimNum(l.freightLine, 2)} override={ov.freight} disabled={locked} onChange={(v) => setOv(l.key, "freight", v)} />
                    </div>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{unit(l.landingUnit)}</td>
                  <td className="px-2 py-1">
                    <div className="flex justify-end">
                      <OverrideCell width="w-[4.5rem]" value={trimNum(l.marginPct)} override={ov.marginPct} disabled={locked} onChange={(v) => setOv(l.key, "marginPct", v)} />
                    </div>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{unit(l.marginUnit)}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-semibold">{money(l.resellUnit, 2)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{money(l.lineTotal)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="sticky bottom-0 bg-s360-bg2 font-semibold text-s360-fg1">
            <tr className="text-right">
              <td colSpan={7} className="px-2 py-1.5 text-left">Totals (INR)</td>
              <td className="px-2 py-1.5 tabular-nums">{money(t.inr)}</td>
              <td />
              <td className="px-2 py-1.5 tabular-nums">{money(t.duty)}</td>
              <td className="px-2 py-1.5 tabular-nums">{money(t.freight)}</td>
              <td className="px-2 py-1.5 tabular-nums">{money(t.landing)}</td>
              <td className="px-2 py-1.5 tabular-nums">{t.marginPct.toFixed(1)}%</td>
              <td className="px-2 py-1.5 tabular-nums">{money(t.margin)}</td>
              <td />
              <td className="px-2 py-1.5 tabular-nums">{money(t.resell)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="m-0 text-[11px] text-s360-fg3">
        Per unit: INR = unit × FX · Duty = INR × duty % (ICEGATE: BCD + AIDC + SWS, IGST excluded) · Landing = INR + duty + freight ÷ qty · Margin = landing × margin % · Resell = landing + margin.
        Tinted cells are changed for this quote only.
      </p>
    </div>
  );

  const review = (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_1fr]">
      <div className="flex flex-col gap-3">
        <Field label="Customer">
          <input className={inputCls} disabled={locked} value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Customer / company name" />
        </Field>
        <Field label="Quotation no." hint={quote ? null : "Leave blank to number it automatically"}>
          <input className={inputCls} disabled={locked} value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)} placeholder={`QT-${new Date().getFullYear()}-0001`} />
        </Field>
        <Field label="Valid until">
          <input type="date" className={inputCls} disabled={locked} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </Field>
        <Field label="Notes / terms (optional)">
          <textarea className={`${inputCls} h-16 py-1.5`} disabled={locked} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payment terms, GST extra, etc." />
        </Field>

        <div className="rounded-md border border-s360-stroke1 p-3">
          <div className="mb-2 text-xs font-semibold text-s360-fg2">Final review</div>
          {issues.length === 0 ? (
            <div className="flex items-center gap-1.5 text-xs text-emerald-700">
              <CheckCircle2 className="w-4 h-4" /> All checks passed
            </div>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-xs">
              {issues.map((i) => (
                <li key={i.key} className={`flex gap-1.5 ${i.level === "error" ? "text-rose-700" : "text-amber-800"}`}>
                  {i.level === "error" ? <XCircle className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
                  <span>
                    {i.text}
                    {i.lines && (
                      <span className="text-s360-fg3">
                        {" "}· line{i.lines.length > 1 ? "s" : ""} {i.lines.slice(0, 12).join(", ")}
                        {i.lines.length > 12 ? ` +${i.lines.length - 12} more` : ""}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-center justify-between rounded-md bg-s360-bg2 px-3 py-2 text-xs">
          <span className="text-s360-fg2">
            Customer quotation · <strong className="text-s360-fg1">{customerQuote.quotedCount}</strong> of {customerQuote.totalCount} lines priced
          </span>
          <span className="font-mono text-[13px] font-bold text-s360-ink">INR {money(customerQuote.total)}</span>
        </div>
        <div className="max-h-[48vh] overflow-auto rounded-md border border-s360-stroke1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white text-left text-s360-fg3">
              <tr>
                {["#", "MPN", "Quoted MPN", "Make", "Qty", "Unit (INR)", "Total (INR)", "SPQ", "MOQ", "Suggested QTY", "Lead time", "Status"].map((h, i) => (
                  <th key={h} className={`px-2.5 py-1.5 font-semibold ${i >= 4 && i <= 9 ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-s360-bg2">
              {customerQuote.lines.map((l) => (
                <tr key={l.key} className={l.quoted ? "" : "text-s360-fg3"}>
                  <td className="px-2.5 py-1.5 tabular-nums text-s360-fg3">{l.slNo}</td>
                  <td className="px-2.5 py-1.5 font-mono">{l.mpn}</td>
                  <td className="px-2.5 py-1.5 font-mono">{l.quoted ? l.quotedMpn : "TBA"}</td>
                  <td className="px-2.5 py-1.5">{l.quoted ? l.quotedMake : ""}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{l.qty.toLocaleString("en-IN")}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{l.quoted ? money(l.unitPrice) : "TBA"}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{l.quoted ? money(l.lineTotal) : ""}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{l.quoted ? l.spq : ""}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{l.quoted ? l.moq : ""}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{l.quoted ? l.availability.toLocaleString("en-IN") : ""}</td>
                  <td className="px-2.5 py-1">
                    {l.quoted && (
                      <OverrideCell
                        left
                        width="w-28"
                        value={l.leadTime}
                        override={(overrides[l.key] || {}).leadTime}
                        disabled={locked}
                        onChange={(v) => setOv(l.key, "leadTime", v)}
                      />
                    )}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <StatusBadge line={l} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // ------------------------------------------------------------------ view --

  const canDownload = worksheet.quotedCount > 0 && Number(fxRate) > 0 && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="flex h-[92vh] max-w-[95vw] flex-col gap-3 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Quotation
            {quote && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  locked ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                }`}
              >
                {locked && <Lock className="w-3 h-3" />}
                {quote.quote_number} · {quote.status}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Landed cost (INR + duty + freight) plus margin gives the resell price. Duty and margin default from your Settings page.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-s360-stroke1 pb-2">
          <div className="flex gap-1">
            {STEPS.map((label, i) => (
              <button
                key={label}
                type="button"
                onClick={() => setStep(i)}
                className={`rounded-md px-3 py-1.5 text-[13px] font-semibold ${
                  step === i ? "bg-s360-violet text-white" : "text-s360-fg2 hover:bg-s360-bg2"
                }`}
              >
                {i + 1}. {label}
              </button>
            ))}
          </div>
          <select
            className={`${inputCls} w-72`}
            value={quote?.id ?? "new"}
            onChange={(e) => onPickSaved(e.target.value)}
            disabled={loading || !!busy}
            aria-label="Saved quotations"
          >
            <option value="new">New quotation</option>
            {savedList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.quote_number} · {s.status}
                {s.customer ? ` · ${s.customer}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center text-sm text-s360-fg3">Loading quotation…</div>
          ) : step === 0 ? (
            sourcing
          ) : step === 1 ? (
            worksheetStep
          ) : (
            review
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-s360-stroke1 pt-3">
          <div className="flex gap-2">
            {step > 0 && (
              <button type="button" className={btn} onClick={() => setStep(step - 1)}>
                Back
              </button>
            )}
            {step < STEPS.length - 1 && (
              <button type="button" className={btnPrimary} onClick={() => setStep(step + 1)}>
                Next
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btn} disabled={!canDownload} onClick={() => download("worksheet")} title="Internal costing sheet with formulas">
              <FileSpreadsheet className="w-4 h-4" />
              {busy === "worksheet" ? "Preparing…" : "Worksheet"}
            </button>
            {step === STEPS.length - 1 && (
              <>
                <button type="button" className={btn} disabled={!canDownload} onClick={() => download("xlsx")}>
                  <FileSpreadsheet className="w-4 h-4" />
                  {busy === "xlsx" ? "Preparing…" : "Quote Excel"}
                </button>
                <button type="button" className={btn} disabled={!canDownload} onClick={() => download("pdf")}>
                  <FileText className="w-4 h-4" />
                  {busy === "pdf" ? "Preparing…" : "Quote PDF"}
                </button>
              </>
            )}
            {!locked && (
              <button type="button" className={btn} disabled={!!busy || loading || !(Number(fxRate) > 0) || worksheet.totalCount === 0} onClick={() => save()}>
                <Save className="w-4 h-4" />
                {busy === "save" ? "Saving…" : "Save draft"}
              </button>
            )}
            {!locked && step === STEPS.length - 1 && (
              <button type="button" className={btnPrimary} disabled={!!busy || loading || hasErrors} onClick={submit} title={hasErrors ? "Fix the errors in the review first" : ""}>
                <Send className="w-4 h-4" />
                {busy === "submit" ? "Submitting…" : "Submit"}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuotationDialog;
